import classnames from 'classnames';
import dynamic from 'next/dynamic';
import React, { RefCallback, useCallback, useState } from 'react';
import { useBooleanFlags } from '@/core/flags/flags.hooks';
import { Text } from '@/core/tamagoshi/Text/Text';
import { translate } from '@/domains/core/localization/translate';
import { Chip } from '@/domains/core/tamagoshiTailwind/components/Chip/Chip';
import { Gtm } from '@/domains/core/tracking/utils/Gtm';
import type { VariantModalProps } from '@/domains/productDiscovery/ProductPage/b2c/components/VariantPicker/components/VariantModal/VariantModal';
import { GTM_CLICK_ON_MODELS_SELECTION, GTM_SWITCH_MODEL } from '@/domains/productDiscovery/ProductPage/b2c/modules/Variants/gtm';
import { useVariantNavigation } from '@/domains/productDiscovery/ProductPage/b2c/modules/Variants/useVariantNavigation';
import { valueIsColor } from '@/domains/productDiscovery/ProductPage/b2c/modules/Variants/VariantSelector/util';
import { StringAbTests } from '@/domains/productDiscovery/ProductPage/constants';
import { useDetail } from '@/domains/productDiscovery/ProductPage/hooks/useDetail';
import styles from './compactVariantSelector.module.scss';
const DynamicVariantModal = dynamic<VariantModalProps>(() => import('@/domains/productDiscovery/ProductPage/b2c/components/VariantPicker/components/VariantModal/VariantModal').then(mod => mod.VariantModal));
const {
  availableOptions
} = translate({
  "availableOptions": "Available options"
});
export const CompactVariantSelector = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const push = useVariantNavigation();
  const {
    variants,
    title
  } = useDetail();
  const ref = useCallback<RefCallback<HTMLDivElement>>(element => {
    const width = element?.clientWidth || 320;
    setVisibleChips(Math.floor(width / 100));
  }, []);
  const [visibleChips, setVisibleChips] = useState<number>(3);
  const [isProductPageRevampEnabled] = useBooleanFlags([StringAbTests.ENABLE_PRODUCT_PAGE_REVAMP]);
  const onViewMoreClick = () => {
    Gtm.push(GTM_CLICK_ON_MODELS_SELECTION('selection_block_list'));
    setIsModalVisible(true);
  };
  const onVariantClick = (modelId: number) => {
    Gtm.push(GTM_SWITCH_MODEL('selection_block_list', title));
    push(modelId);
  };
  const attributes = variants?.list || [];
  const selectedAttributes = variants?.selectedValues || [];
  return <div className={styles.container} ref={ref}>
      {!isProductPageRevampEnabled && <Text type="body1" weight="bold">
          {availableOptions}
        </Text>}
      {attributes?.map(attribute => {
      const selected = selectedAttributes.find(attr => attr.name === attribute.name);
      const isColor = attribute.values.some(val => valueIsColor(val.value));
      const selectedAttributeValue = selected?.hexaColor || selected?.label;
      const visibleAttributes = attribute.values.slice(0, visibleChips);
      const hasMoreValues = attribute.values.length > visibleChips;
      return <div className={classnames({
        [styles.attribute]: true,
        [styles.legacy]: !isProductPageRevampEnabled
      })} key={attribute.name}>
            <Text type="body2" weight="regular">
              <span className={styles.bold}>{attribute.name}:</span>{' '}
              {selected?.label || ''}
            </Text>
            <div className={styles.values}>
              {visibleAttributes.map(value => {
            const chipProps = isColor ? {
              color: value.value,
              text: value.label?.trim() || value.value,
              selected: value.value === selectedAttributeValue,
              unavailable: !value.isAvailable,
              onClick: () => onVariantClick(value.modelId)
            } : {
              text: value.label?.trim() || value.value,
              selected: value.value === selectedAttributeValue,
              unavailable: !value.isAvailable,
              onClick: () => onVariantClick(value.modelId)
            };
            return <Chip key={value.value} {...chipProps} />;
          })}
              {hasMoreValues && <Chip key={attribute.name} text={`+${attribute.values.length - visibleChips}`} onClick={onViewMoreClick} selected={!visibleAttributes.some(value => value.value === selectedAttributeValue)} />}
            </div>
          </div>;
    })}
      {isModalVisible && <DynamicVariantModal onClose={() => setIsModalVisible(false)} />}
    </div>;
};